import queryString from 'query-string';
import fetch from 'globals/wa-fetch';
import { getUser } from 'stores/user-store';
import type { User } from './types';
import { getRootHost } from 'globals/helpers';
import { getStore } from 'app/hermes-redux';
import { setCookie, getCookie } from 'globals/cookieManager';
import {
  getDomainSuffixFromHost,
  getDomainSuffixAndPathFromHref,
} from 'router/navigation';
// Legacy logins function - To refactor properly
import getErrorMessage from 'constants/error-messages';
import isEmpty from 'lodash/isEmpty';

import * as Storage from 'globals/storage';
import Language from 'language';
import { DatadogClient, AuthClient } from '@workivate/tho-web-shared';
import { appendAllTestEnvironmentConfigParams } from 'globals/fetch.helpers';

export type AuthData = {
  refreshToken: string;
  clientId: string;
  tenancyHash: string;
};

// TODO: remove depended when API is updated - 2018-02-28
export const isDependantAccount = (user: User) =>
  user['account_type'] === 'depended' || user['account_type'] === 'dependant';

export const isLimitedAccount = (user: User) =>
  user['account_type'] === 'limited';

export const getUserNotificationEmailAddress = (user: User) =>
  user[user['notification_email_field']];

const WAM_SUBDOMAIN_COOKIE = 'WAM_SUBDOMAIN';

export const setSubDomainCookie = subdomain => {
  const in1year = 365 * 24 * 60 * 60;
  const domain = '.' + getRootHost();
  setCookie(WAM_SUBDOMAIN_COOKIE, subdomain, in1year, domain);
};

export const getSubdomainCookie = () => {
  return getCookie(WAM_SUBDOMAIN_COOKIE) || null;
};

/**
 * Redirects to the subdomain if it is set in the cookie.
 */
export const handleSubdomainRedirect = async () => {
  const subdomain = getSubdomainCookie();
  const host = window.location.host; // Host gives port number also. hostname does not
  const splitHost = host.split('.');
  if (subdomain && (splitHost[0] === 'app' || splitHost[0] === 'login')) {
    splitHost.shift();
    const newUrl =
      '//' +
      subdomain +
      '.' +
      splitHost.join('.') +
      window.location.pathname +
      window.location.search +
      window.location.hash;

    DatadogClient.logMessage(`FE is redirecting to : ${newUrl}`);

    window.location.href = newUrl;
  }
};

export const handleLoginRedirect = (
  companySubdomain: string,
  isPostSignup = false,
  path?: string,
) => {
  // Extract hash fragment from current URL to preserve it during redirect
  const hashFragment = window.location.hash;

  // After signup we want to redirect without any url path otherwise we'll end up back
  // on the signup page rather than logging in.
  const suffix = isPostSignup
    ? getDomainSuffixFromHost()
    : getDomainSuffixAndPathFromHref();
  const domain = '.' + getRootHost();
  const tokenKey = Storage.getKeys().token;
  const token = getToken() || getSignupSession();
  const persistence = Storage.getPersistence();
  const authData = { [tokenKey]: token };
  const msTeamsUrl = getMsTeamsUrl();

  setCookie('WAM_AUTH', JSON.stringify(authData), 5, domain);
  setSubDomainCookie(companySubdomain);

  // Clean current data
  clearLogoutStorage();

  const hasQueryParams = /[?&]/.test(suffix);

  const queryParams: string[] = [];

  if (persistence) {
    queryParams.push(`persistence=${persistence}`);
  }

  if (Language.getLoginLanguagePersistence()) {
    queryParams.push(`lang=${Language.getLocale()}`);
  }

  if (msTeamsUrl) {
    queryParams.push(`msTeamsReturnUrl=${msTeamsUrl}`);
  }

  // When USL is disabled, we need to make sure that we carry over the existing localStorage configurations on Test
  appendAllTestEnvironmentConfigParams({ queryParams });

  // Client-side redirect to the correct subdomain.
  // Preserve hash fragment by appending it to the final URL
  // Hash fragments should come after query parameters according to RFC 3986
  const redirectUrl = `//${companySubdomain}${suffix}${path || ''}${
    hasQueryParams ? '&' : '?'
  }${queryParams.join('&')}${hashFragment || ''}`;

  window.location.href = redirectUrl;
};

/**
 * Returns the redirectUri with the correct lang parameter appended
 * @param language e.g.: en-CA | en_CA
 * @returns
 */
export const getOauth2RedirectUri = () => {
  // this is the Hermes URL that the user will be redirected to after they have successfully logged in with USL
  const redirectUri = location.protocol + '//' + location.host;

  return redirectUri;
};

// gets a one time token which can be used to authenticate with Unified Login
// used in 'ghost' login to Admin Panel
// also for login after signing up with the old auth system in Hercules
// it checks for a WAM-Token in the params
// if none is found, wa-fetch will default to the JWT (Unified Login)
export const getOneTimeToken = async ({ token }: { token?: string | null }) => {
  const userId = getUser()['user_id'];

  const redirectionSecondsLimit = 30;

  const expirationDate = new Date();

  expirationDate.setSeconds(
    expirationDate.getSeconds() + redirectionSecondsLimit,
  );

  const expirationTimestamp = parseInt(
    (expirationDate.getTime() / 1000).toFixed(0),
  );

  const { body: wamToOttResponse } = await fetch(
    `/users/${userId}/wam-to-ott`,
    {
      method: 'post',
      version: 1.3,
      body: {
        expiration_date: expirationTimestamp,
      },
      ...(token && {
        headers: {
          'Wam-Token': token, // use the token if passed. Otherwise wa-fetch will use the JWT
        },
      }),
    },
  );

  const { token: oneTimeToken } = wamToOttResponse || {};

  return oneTimeToken;
};

export const getLoginErrorMessage = (result: any) => {
  const code = result?.error?.code;

  if (!code) {
    return polyglot.t('api_errors.something_wrong');
  }

  return code === -4 && !isEmpty(result.error.fields.email)
    ? getErrorMessage(-1041)
    : getErrorMessage(code);
};

// This check applies after the app and user data have loaded and is a simple UI check used by React components
// It does not check the validity of the session
// It also can return true in signup flows where the user is not yet authenticated
// IMPORTANT if you need to check if the user is authenticated, check !!getEffectiveAuthToken() as well
export const isLoggedIn = () => getStore().getState().session.user !== null;

// This is a stricter check than isLoggedIn() which we are not going to remove due to potential side effects
// It checks both that the user object exists and that there is a valid auth token
// This is used in places where we need to be sure the user is really authenticated rather than UI-only checks
export const isUserLoggedIn = () =>
  !isEmpty(getUser()) && !!getEffectiveAuthToken();

// Get and set the Wam-Token used by the old login system.
// This determines if the user is really authenticated
export const getToken = (): string | null =>
  Storage.get(Storage.getKeys().token);

export const setToken = (token: string) =>
  token && Storage.set(Storage.getKeys().token, token);

// Check if the user is in a Sign Up flow
// At present, Sign Up uses Wam-Token to fetch consents and any other data even if USL Login is enabled.
// This may be removed once Sign Up is fully migrated to the USL Front End
export const getSignupSession = () => {
  // Try current persistence setting first
  let token = Storage.get(Storage.getKeys().signupSession);

  // If not found, try the opposite storage location
  // This handles cases where persistence setting changed after token was stored
  if (!token) {
    const currentPersistence = Storage.getPersistence();
    token = Storage.get(Storage.getKeys().signupSession, !currentPersistence);
  }

  return token;
};

// Check which auth system is being used and get the correct token, so we can check user status
// If the user is logged in with USL, we use AuthClient.refreshToken
// If the user is logged in with the old login system, we also use the Wam-Token
// TODO This is a temporary solution until we fully migrate to USL
export const getEffectiveAuthToken = () =>
  AuthClient.refreshToken || getToken() || undefined;

// SSO HELPERS
export const getSSOConfig = () => {
  const { company } = getStore().getState();
  return company.sso || {};
};

export const isSSO = () => getUser()['is_sso_login'];

export const getSSOLoginLink = (
  locale: string,
  returnUrl: string,
  redirectUrl?: string,
) => {
  const sso = getSSOConfig();

  if (!isEmpty(sso)) {
    const translatedUrl = sso?.translated_login_urls?.[locale];

    const url = translatedUrl || sso['authentication_entry_point_url'];
    const hasQueryParams = !!url?.split('?')[1];
    const relayUrl = `${returnUrl}${
      redirectUrl
        ? `%26${queryString.stringify({
            return_to: redirectUrl,
          })}`
        : ''
    }`;

    return `${url}${hasQueryParams ? '&' : '?'}RelayState=${relayUrl}`;
  }
};

export const getSSOloginNoticeImageUrl = () => {
  const sso = getSSOConfig();

  if (!isEmpty(sso)) {
    const urlObj = sso['login_notice_image_url'];

    return !!urlObj && urlObj;
  }
};

export const getSSOLoginSystemDisplayName = () => {
  const sso = getSSOConfig();

  if (!isEmpty(sso)) {
    return sso['login_system_display_name'] || '';
  }
};

export const getSSOLogoutLink = (locale: string) => {
  const sso = getSSOConfig();
  const translatedUrl = (sso['translated_logout_urls'] || {})[locale];

  return translatedUrl || sso['authentication_exit_point_url'];
};

export const setSSOToken = (token: string) =>
  token && Storage.set(Storage.getKeys().SSOToken, token);
export const getSSOToken = (): string | null =>
  Storage.get(Storage.getKeys().SSOToken);

export const setMsTeamsUrl = (url: string) => {
  return url && Storage.set(Storage.getKeys().msTeamsReturnUrl, url, false);
};

export const getMsTeamsUrl = () => {
  return Storage.get<string>(Storage.getKeys().msTeamsReturnUrl, false);
};

export const setIsFromTeams = (isFromTeams: boolean) => {
  return (
    isFromTeams &&
    Storage.set(Storage.getKeys().isFromTeams, isFromTeams, false)
  );
};

export const getIsFromTeams = () => {
  return Storage.get(Storage.getKeys().isFromTeams, false);
};

export const clearLogoutStorage = () => {
  // cleaning up the storage
  Storage.remove(Storage.getKeys().token);
  Storage.remove(Storage.getKeys().signupSession);
  Storage.remove(Storage.getKeys().signupResponse);
  Storage.remove(Storage.getKeys().signupCompany);
  Storage.remove(Storage.getKeys().SSOToken);
  Storage.remove(Storage.getKeys().persistence);
  Storage.remove(Storage.getKeys().pensionBenefitsDontShowProceedMessage);
  Storage.remove(Storage.getKeys().isProfileEditDisclaimerTooltipDismissed);
  Storage.remove(Storage.getKeys().retailerRedirectPayload);
  Storage.remove(Storage.getKeys().useAssessmentsGlobalContent);
  Storage.remove(Storage.getKeys().isCreatingLimitedAccount);
  Storage.remove(Storage.getKeys().tokenExpiryTime);
  Storage.remove(Storage.getKeys().isFromTeams);
  Storage.remove(Storage.getKeys().tenancyHash);
  Storage.remove(Storage.getKeys().appointmentContactDetails);
  Storage.remove(Storage.getKeys().shouldPersistLoginPageLanguage);
};

// Clean up signup tokens after successful MFA completion and USL integration
export const clearSignupStorage = () => {
  Storage.remove(Storage.getKeys().token);
  Storage.remove(Storage.getKeys().signupSession);
  Storage.remove(Storage.getKeys().signupResponse);
  Storage.remove(Storage.getKeys().signupCompany);
};

export const getEnvironmentForHeader = (): string => {
  if (IS_PRODUCTION) {
    return 'production';
  } else if (IS_INTEGRATION) {
    return 'integration';
  } else if (IS_DEVELOPMENT) {
    return 'development';
  } else if (IS_TEST) {
    return 'test';
  } else {
    return 'unknown';
  }
};
